import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { FileText, Download, Printer, Info, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const formatINR = (n, dec = 0) => {
  if (n === null || n === undefined || isNaN(n)) return '₹0';
  const sign = n < 0 ? '-' : '';
  const v = Math.abs(n);
  return sign + '₹' + v.toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};

const formatLakhCr = (n) => {
  if (!n) return '₹0';
  const sign = n < 0 ? '-' : '';
  const v = Math.abs(n);
  if (v >= 10000000) return `${sign}₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `${sign}₹${(v / 100000).toFixed(2)} L`;
  return `${sign}₹${Math.round(v).toLocaleString('en-IN')}`;
};

const CapitalGains = ({ user, setUser }) => {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [selectedFY, setSelectedFY] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [membersRes, gainsRes] = await Promise.all([
        api.get('/family-members'),
        api.get('/capital-gains', {
          params: {
            ...(selectedMember && { member_id: selectedMember }),
            ...(selectedFY && { financial_year: selectedFY }),
          },
        }),
      ]);
      setMembers(membersRes.data || []);
      setData(gainsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load capital gains');
    } finally {
      setLoading(false);
    }
  }, [selectedMember, selectedFY]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const gains = data?.capital_gains || [];
  const summary = data?.summary || { stcg: { gain: 0, count: 0 }, ltcg: { gain: 0, count: 0 } };
  const taxEst = data?.tax_estimate || {};
  const fysAvailable = data?.financial_years_available || [];

  const stcgRows = useMemo(() => gains.filter(g => !g.is_long_term), [gains]);
  const ltcgRows = useMemo(() => gains.filter(g => g.is_long_term), [gains]);

  const exportToExcel = () => {
    if (!gains.length) { toast.error('No data to export'); return; }
    const wb = XLSX.utils.book_new();

    // ITR-friendly summary sheet
    const summaryRows = [
      ['Capital Gains Summary', '', ''],
      ['Financial Year', selectedFY || 'All', ''],
      ['Member', selectedMember ? members.find(m => m.id === selectedMember)?.name : 'All Members', ''],
      ['', '', ''],
      ['Category', 'Count', 'Gain (₹)'],
      ['Short-Term Capital Gains (STCG)', summary.stcg.count, summary.stcg.gain],
      ['Long-Term Capital Gains (LTCG)', summary.ltcg.count, summary.ltcg.gain],
      ['Total Realized P&L', stcgRows.length + ltcgRows.length, summary.stcg.gain + summary.ltcg.gain],
      ['', '', ''],
      ['Tax Estimate', '', ''],
      ['STCG Tax @ 15%', '', taxEst.stcg_tax || 0],
      ['LTCG Exemption (₹1.25 L)', '', -125000],
      ['LTCG Taxable', '', taxEst.ltcg_taxable || 0],
      ['LTCG Tax @ 12.5%', '', taxEst.ltcg_tax || 0],
      ['Total Tax Liability', '', taxEst.total_tax || 0],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // STCG sheet (ITR Schedule CG - Section A)
    const stcgHeader = ['Symbol', 'Member', 'Quantity', 'Buy Date', 'Buy Price', 'Sell Date', 'Sell Price', 'Cost (₹)', 'Proceeds (₹)', 'Gain/Loss (₹)', 'Days Held', 'FY'];
    const stcgData = [stcgHeader, ...stcgRows.map(r => [
      r.symbol, r.member_name, r.quantity, r.purchase_date, r.purchase_price,
      r.sell_date, r.sell_price, r.cost, r.proceeds, r.gain, r.holding_period_days, r.financial_year,
    ])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(stcgData), 'STCG (Section A)');

    // LTCG sheet (ITR Schedule CG - Section B)
    const ltcgHeader = ['Symbol', 'Member', 'Quantity', 'Buy Date', 'Buy Price', 'Sell Date', 'Sell Price', 'Cost (₹)', 'Proceeds (₹)', 'Gain/Loss (₹)', 'Days Held', 'FY'];
    const ltcgData = [ltcgHeader, ...ltcgRows.map(r => [
      r.symbol, r.member_name, r.quantity, r.purchase_date, r.purchase_price,
      r.sell_date, r.sell_price, r.cost, r.proceeds, r.gain, r.holding_period_days, r.financial_year,
    ])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ltcgData), 'LTCG (Section B)');

    const fname = `Capital_Gains_${selectedFY || 'AllFYs'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success(`Exported to ${fname}`);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="text-center py-20" data-testid="loading">Loading capital gains...</div>
      </Layout>
    );
  }

  const totalPnL = (summary.stcg.gain || 0) + (summary.ltcg.gain || 0);

  const renderRow = (r, idx) => (
    <tr key={`${r.transaction_id}-${idx}`} className="border-b hover:bg-gray-50">
      <td className="py-2 px-3 font-medium">{r.symbol}</td>
      <td className="py-2 px-3 text-sm">{r.member_name}</td>
      <td className="py-2 px-3 text-right">{r.quantity.toLocaleString('en-IN')}</td>
      <td className="py-2 px-3 text-sm text-gray-600">{r.purchase_date || '—'}</td>
      <td className="py-2 px-3 text-right">₹{r.purchase_price.toFixed(2)}</td>
      <td className="py-2 px-3 text-sm text-gray-600">{r.sell_date}</td>
      <td className="py-2 px-3 text-right">₹{r.sell_price.toFixed(2)}</td>
      <td className="py-2 px-3 text-right text-gray-700">{formatINR(r.cost)}</td>
      <td className="py-2 px-3 text-right text-gray-700">{formatINR(r.proceeds)}</td>
      <td className="py-2 px-3 text-right text-sm">{r.holding_period_days}d</td>
      <td className={`py-2 px-3 text-right font-semibold ${r.gain >= 0 ? 'text-green-700' : 'text-red-600'}`}>
        {r.gain >= 0 ? '+' : ''}{formatINR(r.gain)}
      </td>
      <td className="py-2 px-3 text-sm text-gray-500">{r.financial_year}</td>
    </tr>
  );

  return (
    <Layout user={user} setUser={setUser}>
      <div data-testid="capital-gains-page" className="print:p-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3 print:hidden">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-1">Capital Gains Report</h1>
            <p className="text-gray-600 text-sm">FIFO-based realized gains • ITR-ready • {gains.length} entries</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="fy-filter"
              className="input-field text-sm"
              value={selectedFY}
              onChange={(e) => setSelectedFY(e.target.value)}
            >
              <option value="">All FYs</option>
              {fysAvailable.map(fy => <option key={fy} value={fy}>{fy}</option>)}
            </select>
            <select
              data-testid="member-filter-select"
              className="input-field text-sm"
              value={selectedMember}
              onChange={(e) => setSelectedMember(e.target.value)}
            >
              <option value="">All Members</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <button
              onClick={exportToExcel}
              className="btn-primary flex items-center gap-1 text-sm"
              data-testid="export-excel-btn"
            >
              <Download size={16} /> Export ITR
            </button>
            <button
              onClick={handlePrint}
              className="btn-outline flex items-center gap-1 text-sm"
              data-testid="print-btn"
            >
              <Printer size={16} /> Print
            </button>
          </div>
        </div>

        {/* Print header */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold">Capital Gains Report</h1>
          <p className="text-sm text-gray-600">
            {selectedFY ? `Financial Year: ${selectedFY}` : 'All Financial Years'}
            {selectedMember && ` • ${members.find(m => m.id === selectedMember)?.name}`}
            {' • Generated: '}{new Date().toLocaleDateString('en-IN')}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card p-4 bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="summary-total">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="text-indigo-600" size={16} />
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Realized P&L</p>
            </div>
            <p className={`text-2xl font-bold ${totalPnL >= 0 ? 'text-green-700' : 'text-red-600'}`}>
              {formatLakhCr(totalPnL)}
            </p>
            <p className="text-xs text-gray-500 mt-1">{gains.length} sells</p>
          </div>
          <div className="card p-4 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="summary-stcg">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Short-Term (≤ 12 mo)</p>
            <p className={`text-2xl font-bold ${summary.stcg.gain >= 0 ? 'text-amber-700' : 'text-red-600'}`}>
              {formatLakhCr(summary.stcg.gain)}
            </p>
            <p className="text-xs text-gray-500 mt-1">{summary.stcg.count} entries • Tax @ 15%</p>
          </div>
          <div className="card p-4 bg-gradient-to-br from-green-50 to-emerald-50" data-testid="summary-ltcg">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Long-Term (&gt; 12 mo)</p>
            <p className={`text-2xl font-bold ${summary.ltcg.gain >= 0 ? 'text-green-700' : 'text-red-600'}`}>
              {formatLakhCr(summary.ltcg.gain)}
            </p>
            <p className="text-xs text-gray-500 mt-1">{summary.ltcg.count} entries • Tax @ 12.5%</p>
          </div>
          <div className="card p-4 bg-gradient-to-br from-rose-50 to-red-50" data-testid="summary-tax">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="text-rose-600" size={16} />
              <p className="text-xs text-gray-500 uppercase tracking-wider">Estimated Tax</p>
            </div>
            <p className="text-2xl font-bold text-rose-700">{formatLakhCr(taxEst.total_tax)}</p>
            <p className="text-xs text-gray-500 mt-1">After ₹1.25L LTCG exemption</p>
          </div>
        </div>

        {/* Tax breakdown (always visible if data) */}
        {gains.length > 0 && (
          <div className="card p-4 mb-6 bg-blue-50/40 border-blue-200" data-testid="tax-breakdown">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2"><Info size={16} className="text-blue-600" /> Tax Liability Breakdown</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="bg-white p-2 rounded">
                <p className="text-gray-500 text-xs">STCG Tax @ 15%</p>
                <p className="font-semibold">{formatINR(taxEst.stcg_tax)}</p>
              </div>
              <div className="bg-white p-2 rounded">
                <p className="text-gray-500 text-xs">LTCG Taxable (after ₹1.25L exemption)</p>
                <p className="font-semibold">{formatINR(taxEst.ltcg_taxable)}</p>
              </div>
              <div className="bg-white p-2 rounded">
                <p className="text-gray-500 text-xs">LTCG Tax @ 12.5%</p>
                <p className="font-semibold">{formatINR(taxEst.ltcg_tax)}</p>
              </div>
              <div className="bg-rose-100 p-2 rounded border border-rose-300">
                <p className="text-rose-700 text-xs font-medium">Total Tax</p>
                <p className="font-bold text-rose-700">{formatINR(taxEst.total_tax)}</p>
              </div>
            </div>
          </div>
        )}

        {gains.length === 0 ? (
          <div className="card text-center py-12" data-testid="empty-gains">
            <FileText className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-gray-500">No capital gains for the selected filters.</p>
          </div>
        ) : (
          <>
            {/* STCG Section */}
            {stcgRows.length > 0 && (
              <div className="card mb-6" data-testid="stcg-section">
                <div className="p-3 bg-amber-50 border-b border-amber-200">
                  <h2 className="font-bold text-gray-800">Short-Term Capital Gains (STCG) — ITR Schedule CG, Section A</h2>
                  <p className="text-xs text-gray-600 mt-1">{stcgRows.length} entries • Total: {formatLakhCr(summary.stcg.gain)}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-semibold">Symbol</th>
                        <th className="text-left py-2 px-3 font-semibold">Member</th>
                        <th className="text-right py-2 px-3 font-semibold">Qty</th>
                        <th className="text-left py-2 px-3 font-semibold">Buy Date</th>
                        <th className="text-right py-2 px-3 font-semibold">Buy ₹</th>
                        <th className="text-left py-2 px-3 font-semibold">Sell Date</th>
                        <th className="text-right py-2 px-3 font-semibold">Sell ₹</th>
                        <th className="text-right py-2 px-3 font-semibold">Cost</th>
                        <th className="text-right py-2 px-3 font-semibold">Proceeds</th>
                        <th className="text-right py-2 px-3 font-semibold">Days</th>
                        <th className="text-right py-2 px-3 font-semibold">Gain/Loss</th>
                        <th className="text-left py-2 px-3 font-semibold">FY</th>
                      </tr>
                    </thead>
                    <tbody>{stcgRows.map(renderRow)}</tbody>
                  </table>
                </div>
              </div>
            )}

            {/* LTCG Section */}
            {ltcgRows.length > 0 && (
              <div className="card" data-testid="ltcg-section">
                <div className="p-3 bg-green-50 border-b border-green-200">
                  <h2 className="font-bold text-gray-800">Long-Term Capital Gains (LTCG) — ITR Schedule CG, Section B</h2>
                  <p className="text-xs text-gray-600 mt-1">{ltcgRows.length} entries • Total: {formatLakhCr(summary.ltcg.gain)}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-semibold">Symbol</th>
                        <th className="text-left py-2 px-3 font-semibold">Member</th>
                        <th className="text-right py-2 px-3 font-semibold">Qty</th>
                        <th className="text-left py-2 px-3 font-semibold">Buy Date</th>
                        <th className="text-right py-2 px-3 font-semibold">Buy ₹</th>
                        <th className="text-left py-2 px-3 font-semibold">Sell Date</th>
                        <th className="text-right py-2 px-3 font-semibold">Sell ₹</th>
                        <th className="text-right py-2 px-3 font-semibold">Cost</th>
                        <th className="text-right py-2 px-3 font-semibold">Proceeds</th>
                        <th className="text-right py-2 px-3 font-semibold">Days</th>
                        <th className="text-right py-2 px-3 font-semibold">Gain/Loss</th>
                        <th className="text-left py-2 px-3 font-semibold">FY</th>
                      </tr>
                    </thead>
                    <tbody>{ltcgRows.map(renderRow)}</tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Footer / disclaimer */}
        <div className="mt-6 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 print:bg-white">
          <p><strong>Methodology:</strong> Gains computed using FIFO (First-In-First-Out) lot matching. Holding period = Sell Date − Buy Date. STCG threshold: 12 months for listed equity.</p>
          <p className="mt-1"><strong>FY26 Tax Rates (post Jul-2024):</strong> STCG @ 15% • LTCG @ 12.5% on gains above ₹1.25 Lakhs (per individual).</p>
          <p className="mt-1 italic">Note: This is a portfolio-level summary. Consult a CA for ITR filing — STT, brokerage, and grandfathering (pre-31 Jan 2018 LTCG) adjustments may apply.</p>
        </div>
      </div>
    </Layout>
  );
};

export default CapitalGains;
